import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

function BlockListPage() {
  const [basePlots, setBasePlots] = useState([]);
  const [basePlotsLoading, setBasePlotsLoading] = useState(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const projectId = queryParams.get("project_id");

  const navigate = useNavigate();


  const basePlotsRef = useRef([]);


  const refreshingRef = useRef(false);

  const getAuthToken = () => localStorage.getItem("token");


  const formatRemainingTime = (seconds) => {
    const totalSeconds = Math.max(0, Number(seconds || 0));

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}:${String(secs).padStart(2, "0")}`;
  };


  const calculateRemainingFromHoldUntil = (holdUntil) => {
    if (!holdUntil) return 0;

    try {
      let holdDate;


      if (
        typeof holdUntil === "string" &&
        /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(holdUntil)
      ) {
        holdDate = new Date(holdUntil.replace(" ", "T"));
      } else {
        holdDate = new Date(holdUntil);
      }

      if (isNaN(holdDate.getTime())) {
        return 0;
      }

      const now = new Date();

      return Math.max(
        0,
        Math.floor((holdDate.getTime() - now.getTime()) / 1000),
      );
    } catch (error) {
      console.error("Hold date calculation error:", error);
      return 0;
    }
  };


  const fetchBaseProjectPlots = async () => {
    if (!projectId) return;


    if (refreshingRef.current) return;

    refreshingRef.current = true;
    setBasePlotsLoading(true);

    try {
      const token = getAuthToken();

      const response = await fetch(
        `${API_URL}/plot-list-base-project?project_id=${projectId}&page=1&limit=1000`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      console.log("Base Project Plot API Response:", data);

      const plots = (data.data || []).map((plot) => {
        const status = String(plot.status || "").toLowerCase();

        let remainingSeconds = 0;


        if (
          status === "hold" &&
          plot.hold_remaining_seconds !== undefined &&
          plot.hold_remaining_seconds !== null
        ) {
          remainingSeconds = Math.max(
            0,
            Number(plot.hold_remaining_seconds),
          );
        }


        if (status === "hold" && !remainingSeconds && plot.hold_until) {
          remainingSeconds = calculateRemainingFromHoldUntil(
            plot.hold_until,
          );
        }


        if (status === "hold" && remainingSeconds <= 0) {
          return {
            ...plot,
            status: "available",
            hold_remaining_seconds: 0,
            hold_until: null,
          };
        }

        return {
          ...plot,
          hold_remaining_seconds: remainingSeconds,
        };
      });

      basePlotsRef.current = plots;
      setBasePlots(plots);
    } catch (err) {
      console.error("Base project plot fetch error:", err);

      basePlotsRef.current = [];
      setBasePlots([]);
    } finally {
      refreshingRef.current = false;
      setBasePlotsLoading(false);
    }
  };


  useEffect(() => {
    fetchBaseProjectPlots();
  }, [projectId]);


  useEffect(() => {
    if (!projectId) return;

    const timer = setInterval(() => {
      const currentPlots = basePlotsRef.current;

      if (!currentPlots.length) return;

      let hasExpired = false;

      const updatedPlots = currentPlots.map((plot) => {
        const status = String(plot.status || "").toLowerCase();


        if (status !== "hold") {
          return plot;
        }

        let remaining = Number(plot.hold_remaining_seconds || 0);


        if (!remaining && plot.hold_until) {
          remaining = calculateRemainingFromHoldUntil(plot.hold_until);
        }


        remaining = Math.max(0, remaining - 1);


        if (remaining <= 0) {
          hasExpired = true;

          return {
            ...plot,
            status: "available",
            hold_remaining_seconds: 0,
            hold_until: null,
          };
        }

        return {
          ...plot,
          hold_remaining_seconds: remaining,
        };
      });


      basePlotsRef.current = updatedPlots;


      setBasePlots(updatedPlots);


      if (hasExpired) {
        setTimeout(() => {
          fetchBaseProjectPlots();
        }, 200);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [projectId]);


  const handleBookPlot = (plot) => {
    const status = String(plot.status || "").toLowerCase();

    // Safety check
    if (status !== "available") {
      return;
    }

    const plotId = plot.id;

    navigate(
      `/plot-list?project_id=${plot.project_id}&block_id=null&plot_id=${plotId}`,
    );
  };


  if (basePlotsLoading && basePlots.length === 0) {
    return (
      <div className="padding_15 px-0">
        <div className="card">
          <div className="card-header">
            <div className="d-flex align-items-center justify-content-between">
              <div className="titlepage">
                <h3>Project Base Plot List</h3>
              </div>

              <div className="d-flex gap-2">
                <Link
                  to={`/sold-plots-details?project_id=${projectId}`}
                  className="submit_button"
                >
                  History
                </Link>

                <Link to="/all-project" className="backbutton">
                  Back
                </Link>
              </div>
            </div>
          </div>

          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>

            <p className="mt-3">Loading plots...</p>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="padding_15 px-0">
      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center justify-content-between">
            <div className="titlepage">
              <h3>Project Base Plot List</h3>
            </div>

            <div className="d-flex gap-2">
              <Link
                to={`/sold-plots-details?project_id=${projectId}`}
                className="btn btn-primary submit_button border border-white"
              >
                History
              </Link>

              <Link
                to="/all-project"
                className="btn btn-success fw-bold"
              >
                Back
              </Link>
            </div>
          </div>
        </div>

        {/* <div className="card-body">
          {basePlots.length > 0 ? (
            <div className="plot-chessboard">
              <div className="chessboard-grid">
                {basePlots.map((plot) => {
                  const status = String(
                    plot.status || "unknown",
                  ).toLowerCase();

                  
                  const statusClass =
                    status === "available"
                      ? "cell-available"
                      : status === "hold"
                        ? "cell-hold"
                        : status === "active" || status === "booked"
                          ? "cell-booked"
                          : "cell-sold";

                 
                  const holdRemaining = Number(
                    plot.hold_remaining_seconds || 0,
                  );

                  return (
                    <div
                      key={plot.id}
                      className={`chess-cell ${statusClass}`}
                      onClick={() => {
                        if (status === "available") {
                          handleBookPlot(plot);
                        }
                      }}
                      style={{
                        cursor:
                          status === "available"
                            ? "pointer"
                            : "not-allowed",
                      }}
                    >
                      <div className="plot-number">
                        {plot.plot_shop_villa_no || "N/A"}
                      </div>
                      <div className="plot-status">
                        {status === "hold" ? (
                          <>
                            <div className="hold-status-text">
                              HOLD
                            </div>

                            <div className="hold-timer">
                              ⏱{" "}
                              {formatRemainingTime(
                                holdRemaining,
                              )}
                            </div>
                          </>
                        ) : (
                          <div>
                            {plot.status || "unknown"}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-5">
              <div
                style={{
                  fontSize: "48px",
                  marginBottom: "20px",
                }}
              >
                📊
              </div>

              <h4>No Plots Found</h4>

              <p className="text-muted">
                No plots available for this project.
              </p>
            </div>
          )}
        </div> */}

        <div className="card-body">
          {basePlots.length > 0 ? (
            <div className="plot-chessboard">
              <div className="chessboard-grid">
                {basePlots.map((plot) => {
                  const status = String(plot.status || "unknown").toLowerCase();

                  const statusClass =
                    status === "available"
                      ? "cell-available"
                      : status === "hold"
                        ? "cell-hold"
                        : status === "active" || status === "booked"
                          ? "cell-booked"
                          : "cell-sold";

                  const holdRemaining = Number(
                    plot.hold_remaining_seconds || 0
                  );

                  return (
                    <div
                      key={plot.id}
                      className={`chess-cell ${statusClass}`}
                      onClick={() => {
                        if (status === "available") {
                          handleBookPlot(plot);
                        }
                      }}
                      style={{
                        cursor:
                          status === "available"
                            ? "pointer"
                            : "not-allowed",
                        backgroundColor:
                          status === "hold"
                            ? "yellow"
                            : status === "sold"
                              ? "red"
                              : undefined,
                        color:
                          status === "hold"
                            ? "#000"
                            : status === "sold"
                              ? "#fff"
                              : undefined,
                      }}
                    >
                      {/* Plot Number */}
                      <div className="plot-number">
                        {String(
                          plot.plot_shop_villa_no || "N/A"
                        ).toUpperCase()}
                      </div>

                      {/* Plot Status */}
                      <div className="plot-status">
                        {status === "hold" ? (
                          <>
                            <div
                              className="hold-status-text"
                              style={{
                                color: "#000",
                                fontWeight: "bold",
                              }}
                            >
                              HOLD
                            </div>

                            <div className="hold-timer">
                              ⏱{" "}
                              {formatRemainingTime(holdRemaining)}
                            </div>
                          </>
                        ) : (
                          <div>
                            {String(
                              plot.status || "unknown"
                            ).toUpperCase()}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-5">
              <div
                style={{
                  fontSize: "48px",
                  marginBottom: "20px",
                }}
              >
                📊
              </div>

              <h4>No Plots Found</h4>

              <p className="text-muted">
                No plots available for this project.
              </p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default BlockListPage;